import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowLeftRight, ArrowRight, ChevronDown, Eye, GitCompare, History, Loader2, MoveVertical,
  PenLine, RotateCcw, Save, X,
} from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'
import { collectBlockTypes } from './normalizeRegistry'
import { BuilderBlockPreviewFrame } from './BuilderBlockPreview'
import { useDialogFocus } from './hooks/useDialogFocus'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { notify } from '../../notices'
import {
  VERSIONS_CHANGED_EVENT, comparePages, diffWords, fetchVersion, fetchVersions, formatVersionDate, restoreVersion,
} from './versionDiff'

/**
 * Historique des VERSIONS d'une page du builder : la liste, la consultation
 * d'une version, la comparaison de deux états, la restauration.
 *
 * POURQUOI DU REACT (rituel config-first) : tout se passe dans le chrome de
 * l'éditeur, dont l'état (`useBuilder` : page éditée, mode d'édition, flush
 * des DataSync) n'est pas un magasin reactive-json, et la vue de comparaison
 * monte les blocs par le canevas (BuilderBlockMount, par déf du registre) —
 * aucun rjBuild ne peut héberger ça, et le registre de plugins du projet
 * (App.jsx) n'a rien qui monte des blocs du builder. La logique (API, diff)
 * vit à part, dans versionDiff.js.
 *
 * Contrat serveur : PageVersionTrait.php. Une version = une révision Drupal de
 * la page + un instantané du contenu de ses blocs.
 */

const KIND_LABELS = {
  restore: 'Restauration',
  backup: 'Sauvegarde automatique',
}

/** « Version du 7 oct. 2026 à 14:32 » */
const versionName = (v) => (v ? `Version du ${formatVersionDate(v.createdAt)}` : 'Version actuelle')

const registryMap = (registry) => Object.fromEntries((registry ?? []).map(d => [d.type, d]))

/**
 * Les gestes qui ouvrent une consultation, partagés par le panneau et la barre.
 * Une consultation exige une page ENREGISTRÉE : la « version actuelle » qu'on
 * compare est l'état en base, et quitter l'édition démonte les blocs (leurs
 * modifications en mémoire seraient perdues).
 */
const useVersionActions = () => {
  const { page, registry, loadRegistryTypes, setVersionView } = useBuilder()
  const pageNid = page.pageNid

  const openVersion = useCallback(async (vid) => {
    const { version, page: snapshot } = await fetchVersion(pageNid, vid)
    setVersionView({ mode: 'view', key: `v-${vid}`, version, page: snapshot })
  }, [pageNid, setVersionView])

  // `a` / `b` : un vid, ou `'current'`. Le plus ANCIEN va à gauche de la flèche,
  // quel que soit l'ordre du clic : « ajouté » doit vouloir dire « apparu depuis ».
  const openCompare = useCallback(async (a, b) => {
    const rank = (x) => (x === 'current' ? Number.MAX_SAFE_INTEGER : Number(x))
    const [older, newer] = rank(a) <= rank(b) ? [a, b] : [b, a]
    const [from, to] = await Promise.all([fetchVersion(pageNid, older), fetchVersion(pageNid, newer)])
    // Un bloc RETIRÉ depuis n'a plus sa déf au registre (il ne porte que celles
    // de la page) : sans elle, ses champs n'auraient pas de libellé.
    const extra = await loadRegistryTypes(collectBlockTypes([from.page, to.page]))
    setVersionView({
      mode: 'compare',
      key: `c-${older}-${newer}`,
      from: { vid: older, version: from.version },
      to: { vid: newer, version: to.version },
      ...comparePages(from.page, to.page, registryMap([...registry, ...(extra ?? [])])),
    })
  }, [pageNid, registry, loadRegistryTypes, setVersionView])

  return { openVersion, openCompare }
}

// ---------------------------------------------------------------------------
// Panneau d'historique
// ---------------------------------------------------------------------------

/**
 * Le panneau latéral « Historique » : la version actuelle en tête, puis les
 * versions enregistrées. Chacune se CONSULTE ; deux cases cochées (la version
 * actuelle comprise) se COMPARENT.
 */
export const BuilderVersionPanel = ({ onClose }) => {
  const { page } = useBuilder()
  const { openVersion, openCompare } = useVersionActions()
  const [versions, setVersions] = useState(null)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState([])
  const [busy, setBusy] = useState(null)
  const panelRef = useRef(null)
  useDialogFocus(panelRef)
  useEscapeClose(onClose)

  useEffect(() => {
    let alive = true
    fetchVersions(page.pageNid)
      .then(list => { if (alive) setVersions(list) })
      .catch(err => { if (alive) setError(err.message) })
    return () => { alive = false }
  }, [page.pageNid])

  const toggle = (id) => setSelected(prev => (
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(-2)
  ))

  // Un geste à la fois, et le panneau se ferme sur la vue ouverte : c'est le
  // canevas qui montre le résultat, et la barre du builder qui ramène ici.
  const run = async (key, task) => {
    setBusy(key)
    try {
      await task()
      onClose()
    } catch (err) {
      notify({ type: 'error', message: err?.message || 'Opération impossible.' })
    } finally {
      setBusy(null)
    }
  }

  const rows = [
    { id: 'current', current: true },
    ...(versions ?? []).map(v => ({ id: String(v.vid), version: v })),
  ]

  return (
    <>
      <div className="fixed inset-0 z-40 bg-gray-900/20 backdrop-blur-[1px]" onClick={onClose} aria-hidden="true" />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-versions-title"
        className="fixed right-0 top-0 bottom-0 z-50 w-96 max-w-full bg-gray-50 border-l border-gray-200 shadow-2xl flex flex-col animate-slide-in-right"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <div>
            <h2 id="builder-versions-title" className="text-sm font-semibold text-gray-800">Historique des versions</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Consultez une version, ou cochez-en deux pour les comparer.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            aria-label="Fermer l'historique"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {error ? (
            <p className="text-sm text-rose-700 p-3" role="alert">{error}</p>
          ) : versions === null ? (
            <div className="flex justify-center py-10" role="status"><Loader2 size={20} className="animate-spin text-gray-400" aria-hidden="true" /><span className="sr-only">Chargement de l'historique</span></div>
          ) : (
            <ol className="flex flex-col gap-2" aria-label="Versions de la page">
              {rows.map(row => {
                const v = row.version
                const checked = selected.includes(row.id)
                const name = row.current ? 'Version actuelle' : versionName(v)
                return (
                  <li
                    key={row.id}
                    className={`rounded-lg border bg-white px-3 py-2.5 transition-colors ${checked ? 'border-teal-400 ring-1 ring-teal-400' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        id={`builder-version-${row.id}`}
                        checked={checked}
                        onChange={() => toggle(row.id)}
                        className="mt-0.5 w-4 h-4 accent-teal-700"
                        aria-label={`Sélectionner « ${name} » pour la comparer`}
                      />
                      <div className="min-w-0 flex-1">
                        <label htmlFor={`builder-version-${row.id}`} className="block text-sm font-medium text-gray-800">
                          {name}
                        </label>
                        {row.current ? (
                          <p className="text-xs text-gray-500 mt-0.5">L'état enregistré, celui que vous modifiez.</p>
                        ) : (
                          <>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {v.author || 'Auteur inconnu'} · {v.blockCount} bloc{v.blockCount > 1 ? 's' : ''}
                            </p>
                            {KIND_LABELS[v.kind] && (
                              <p className="mt-1 inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                <RotateCcw size={11} aria-hidden="true" />
                                {KIND_LABELS[v.kind]}
                                {v.kind === 'restore' && v.restoredFrom?.createdAt && (
                                  <span className="font-normal"> de la version du {formatVersionDate(v.restoredFrom.createdAt)}</span>
                                )}
                              </p>
                            )}
                            {v.comment && v.kind !== 'backup' && (
                              <p className="text-xs text-gray-700 mt-1 break-words">« {v.comment} »</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {!row.current && (
                      <div className="mt-2 flex flex-wrap gap-1.5 pl-6">
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => run(`view-${row.id}`, () => openVersion(row.id))}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-700 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Consulter : ${name}`}
                        >
                          {busy === `view-${row.id}` ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Eye size={12} aria-hidden="true" />}
                          Consulter
                        </button>
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => run(`cmp-${row.id}`, () => openCompare(row.id, 'current'))}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-700 border border-gray-200 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Comparer à l'actuelle : ${name}`}
                        >
                          {busy === `cmp-${row.id}` ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <GitCompare size={12} aria-hidden="true" />}
                          Comparer à l'actuelle
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
              {versions.length === 0 && (
                <li className="text-xs text-gray-500 px-1 py-3">
                  Aucune version enregistrée. Cochez « Créer une version » à côté du bouton Save pour en créer une.
                </li>
              )}
            </ol>
          )}
        </div>

        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <button
            type="button"
            disabled={selected.length !== 2 || busy !== null}
            onClick={() => run('compare', () => openCompare(selected[0], selected[1]))}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            {busy === 'compare' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <GitCompare size={14} aria-hidden="true" />}
            {selected.length === 2 ? 'Comparer les deux versions cochées' : `Cochez ${2 - selected.length} version${selected.length === 1 ? '' : 's'} à comparer`}
          </button>
        </div>
      </aside>
    </>
  )
}

// ---------------------------------------------------------------------------
// Barre de consultation (remplace les actions du header)
// ---------------------------------------------------------------------------

const barButton = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-50'

/**
 * La barre du builder pendant une consultation : ce qu'on regarde, et les
 * deux issues — restaurer, ou revenir à l'édition de la version actuelle.
 */
export const BuilderVersionBar = ({ onOpenHistory }) => {
  const { versionView, setVersionView, page, reloadPage } = useBuilder()
  const { openCompare } = useVersionActions()
  const [busy, setBusy] = useState(null)

  const leave = () => setVersionView(null)

  // Le geste qui a ouvert la consultation (bouton du panneau, de la barre) vient
  // d'être démonté : sans reprise, le focus clavier tombait sur <body> (WCAG
  // 2.4.3). On le pose sur la barre, qui annonce ce qu'on regarde.
  const regionRef = useRef(null)
  useEffect(() => { regionRef.current?.focus() }, [versionView.key])

  // Pas de confirmation : le serveur fige d'abord l'état actuel s'il n'est pas
  // déjà une version (« État avant restauration ») — la restauration se défait
  // en restaurant celle-là.
  const restore = async () => {
    setBusy('restore')
    try {
      const result = await restoreVersion(page.pageNid, versionView.version.vid)
      const notes = []
      if (result?.backup) notes.push("l'état précédent a été conservé dans l'historique")
      if (result?.sharedKept) notes.push(`${result.sharedKept} bloc${result.sharedKept > 1 ? 's' : ''} partagé${result.sharedKept > 1 ? 's gardent' : ' garde'} son contenu actuel`)
      if (result?.skipped) notes.push(`${result.skipped} bloc${result.skipped > 1 ? 's' : ''} d'un type supprimé non restauré${result.skipped > 1 ? 's' : ''}`)
      notify({
        type: 'success',
        message: `${versionName(versionView.version)} restaurée${notes.length ? ` — ${notes.join(' ; ')}` : ''}.`,
      })
      window.dispatchEvent(new CustomEvent(VERSIONS_CHANGED_EVENT))
      setVersionView(null)
      reloadPage()
    } catch (err) {
      notify({ type: 'error', message: err?.message || 'La restauration a échoué.' })
    } finally {
      setBusy(null)
    }
  }

  const compareToCurrent = async () => {
    setBusy('compare')
    try {
      await openCompare(String(versionView.version.vid), 'current')
    } catch (err) {
      notify({ type: 'error', message: err?.message || 'Comparaison impossible.' })
    } finally {
      setBusy(null)
    }
  }

  const isView = versionView.mode === 'view'
  const v = versionView.version

  return (
    <div ref={regionRef} tabIndex={-1} className="flex flex-wrap items-center justify-end gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" role="region" aria-label={isView ? 'Consultation d\'une version' : 'Comparaison de versions'}>
      {/* Une ligne, toujours : la description se TRONQUE (texte complet en
          infobulle) plutôt que de pousser les boutons à la ligne. */}
      <p
        className="text-sm text-gray-700 min-w-0 max-w-56 truncate"
        aria-live="polite"
        title={isView ? `${versionName(v)} (lecture seule)${v?.author ? ` · ${v.author}` : ''}${v?.comment && v.kind !== 'backup' ? ` · « ${v.comment} »` : ''}` : undefined}
      >
        {isView ? (
          <>
            <span className="sr-only">Lecture seule : </span>
            <span className="font-semibold">{versionName(v)}</span>
            <span className="text-gray-500">{v?.author ? ` · ${v.author}` : ''}{v?.comment && v.kind !== 'backup' ? ` · « ${v.comment} »` : ''}</span>
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="font-semibold">{versionName(versionView.from.version)}</span>
            <ArrowRight size={14} aria-hidden="true" /><span className="sr-only">comparée à</span>
            <span className="font-semibold">{versionName(versionView.to.version)}</span>
          </span>
        )}
      </p>
      {isView && (
        <>
          <button
            type="button"
            onClick={restore}
            disabled={busy !== null}
            className={`${barButton} border-teal-700 bg-teal-700 text-white hover:bg-teal-800`}
          >
            {busy === 'restore' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={14} aria-hidden="true" />}
            Restaurer cette version
          </button>
          <button
            type="button"
            onClick={compareToCurrent}
            disabled={busy !== null}
            className={`${barButton} border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50`}
          >
            {busy === 'compare' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <GitCompare size={14} aria-hidden="true" />}
            Comparer
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onOpenHistory}
        className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        aria-label="Historique des versions"
        title="Historique des versions"
      >
        <History size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={leave}
        className={`${barButton} border-gray-800 bg-gray-800 text-white hover:bg-gray-900`}
        title="Retour à la modification de la version actuelle"
        aria-label="Retour à la modification de la version actuelle"
      >
        <PenLine size={14} aria-hidden="true" />
        Retour à la modification
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vue de comparaison
// ---------------------------------------------------------------------------

const STATUS = {
  added: { label: 'Ajouté', className: 'builder-diff-added' },
  removed: { label: 'Supprimé', className: 'builder-diff-removed' },
  modified: { label: 'Modifié', className: 'builder-diff-modified' },
  same: { label: 'Inchangé', className: 'builder-diff-same' },
}

/** Un texte avant → après, mot à mot : supprimé barré, ajouté souligné. */
const WordDiff = ({ before, after }) => {
  const parts = useMemo(() => diffWords(before, after), [before, after])
  return (
    <p className="builder-diff-text">
      {parts.map((p, i) => (
        p.op === 'same' ? <span key={i}>{p.text}</span>
          : p.op === 'del' ? <del key={i}>{p.text}</del>
            : <ins key={i}>{p.text}</ins>
      ))}
    </p>
  )
}

const ImageThumb = ({ image, label }) => (
  image?.url
    ? <img src={image.url} alt={image.alt || label} className="builder-diff-thumb" />
    : <span className="builder-diff-thumb builder-diff-thumb-empty">{image ? 'Image' : 'Aucune'}</span>
)

/** Le détail d'un changement : un champ, son avant, son après. */
const ChangeItem = ({ change }) => {
  if (change.kind === 'row') {
    return (
      <li>
        <span className="builder-diff-change-label">{change.label}</span>
        <span className={change.status === 'added' ? 'builder-diff-pill-added' : 'builder-diff-pill-removed'}>
          {change.status === 'added' ? 'ligne ajoutée' : 'ligne supprimée'}
        </span>
        {change.summary && <span className="text-gray-600"> « {change.summary} »</span>}
      </li>
    )
  }
  return (
    <li>
      <span className="builder-diff-change-label">{change.label}</span>
      {change.kind === 'text' ? (
        <WordDiff before={change.before} after={change.after} />
      ) : change.kind === 'format' ? (
        <p className="builder-diff-text">Mise en forme modifiée (gras, lien, titre…) — le texte est identique.</p>
      ) : change.kind === 'image' ? (
        <span className="inline-flex items-center gap-2 mt-1">
          <ImageThumb image={change.before} label="Image avant" />
          <ArrowRight size={14} aria-hidden="true" /><span className="sr-only">devient</span>
          <ImageThumb image={change.after} label="Image après" />
        </span>
      ) : (
        <p className="builder-diff-text">
          {change.before ? <del>{change.before}</del> : <span className="text-gray-400">(vide)</span>}
          {' → '}
          {change.after ? <ins>{change.after}</ins> : <span className="text-gray-400">(vide)</span>}
        </p>
      )}
    </li>
  )
}

/**
 * Surligne, dans le RENDU d'un bloc, les éléments dont la donnée a changé.
 *
 * Les blocs portent déjà la carte de leurs champs : `data-builder-field` (et
 * `data-builder-row-path` pour une cellule de liste), posés par
 * expandEditable — en lecture comme en édition. On s'en sert tels quels.
 * Un bloc se monte en différé (et ses listes dynamiques après) : un
 * MutationObserver repasse quand le DOM arrive.
 */
const useFieldHighlights = (ref, targets, active) => {
  useLayoutEffect(() => {
    const root = ref.current
    if (!root || !active || !targets?.length || typeof CSS === 'undefined') return undefined
    const selectors = targets.map(t => {
      const field = `[data-builder-field="${CSS.escape(t.field)}"]`
      if (t.rowPath == null) return `${field}:not([data-builder-row-path])`
      return t.wholeRow
        ? `${field}[data-builder-row-path^="${CSS.escape(t.rowPath)}."], ${field}[data-builder-row-path="${CSS.escape(t.rowPath)}"]`
        : `${field}[data-builder-row-path="${CSS.escape(t.rowPath)}"]`
    })
    // Repli : un champ composite (lien, objet) n'est pas forcément posé sur un
    // élément à son nom de cellule — on allume alors le champ entier.
    const fallback = [...new Set(targets.map(t => `[data-builder-field="${CSS.escape(t.field)}"]`))]
    const marked = new Set()
    const apply = () => {
      let hits = root.querySelectorAll(selectors.join(', '))
      if (hits.length === 0) hits = root.querySelectorAll(fallback.join(', '))
      hits.forEach(el => {
        if (!marked.has(el)) { el.classList.add('builder-diff-hit'); marked.add(el) }
      })
    }
    apply()
    const observer = new MutationObserver(apply)
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      marked.forEach(el => el.classList.remove('builder-diff-hit'))
    }
  }, [ref, targets, active])
}

/**
 * Un bloc SUPERPOSÉ — un en-tête de site posé en `absolute` par-dessus le
 * bandeau qui le suit — n'occupe aucune hauteur de flux. Dans un cadre confiné
 * (cf. `.builder-diff-frame`, index.css) il serait rogné à zéro et
 * disparaîtrait de la comparaison. On donne alors à son côté la hauteur de
 * son contenu : le bloc s'affiche à sa place, au-dessus du suivant au lieu de
 * le recouvrir. Un bloc en flux (le cas général) n'est pas touché.
 */
const useOverlayHeight = (frameRef) => {
  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame || typeof ResizeObserver === 'undefined') return undefined
    const sides = () => [...frame.querySelectorAll(':scope > .builder-diff-side')]
    const measure = () => {
      for (const el of sides()) {
        el.style.minHeight = ''
        const content = [...el.querySelectorAll('*')].slice(0, 400)
        if (el.clientHeight >= 8 || content.length === 0) continue
        const top = el.getBoundingClientRect().top
        const bottom = Math.max(...content.map(n => n.getBoundingClientRect().bottom))
        if (bottom - top > 8) el.style.minHeight = `${Math.ceil(bottom - top)}px`
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    sides().forEach(el => { observer.observe(el); [...el.children].forEach(c => observer.observe(c)) })
    const mutations = new MutationObserver(() => { measure(); sides().forEach(el => [...el.querySelectorAll('*')].slice(0, 50).forEach(c => observer.observe(c))) })
    mutations.observe(frame, { childList: true, subtree: true })
    return () => { observer.disconnect(); mutations.disconnect() }
  }, [frameRef])
}

/** La cible d'un changement dans le rendu (cf. useFieldHighlights). */
const targetOf = (change) => ({
  field: String(change.path[0]),
  rowPath: typeof change.path[1] === 'number' ? change.path.slice(1).join('.') : null,
  wholeRow: change.kind === 'row',
})

/** Sélecteurs CSS des éléments qui affichent un champ (cf. BuilderRowField). */
const selectorsFor = (path) => {
  const field = `[data-builder-field="${CSS.escape(String(path[0]))}"]`
  if (typeof path[1] === 'number') return [`${field}[data-builder-row-path="${CSS.escape(path.slice(1).join('.'))}"]`]
  return [
    // Champ composite (`cta.label`) : certaines défs le déclarent par son
    // chemin complet, d'autres par le champ porteur.
    ...(path.length > 1 ? [`[data-builder-field="${CSS.escape(path.join('.'))}"]`] : []),
    `${field}:not([data-builder-row-path])`,
  ]
}

const squash = (t) => String(t ?? '').replace(/\s+/g, '')

/**
 * Affiché ? Une pastille `builder-edit-only` porte le champ en édition mais
 * n'existe pas pour le lecteur ; une copie mobile masquée non plus. Annoter
 * l'une d'elles laissait le texte réellement affiché sans marque.
 */
const isShown = (el) => !el.closest('.builder-edit-only') && el.getClientRects().length > 0

/**
 * Marques de RÉVISION dans le rendu : le texte modifié d'un bloc est réécrit,
 * dans l'élément même qui l'affiche, en mots supprimés (barrés) et ajoutés
 * (soulignés) — comme les modifications suivies d'un document.
 *
 * L'élément est retrouvé par la carte des champs que posent les défs
 * (`data-builder-field` / `data-builder-row-path`, cf. expandEditable), à défaut
 * par son texte. Il n'est réécrit QUE si son texte est exactement la nouvelle
 * valeur : un primitif qui met en forme sa valeur (troncature, compteur…) garde
 * son rendu, et le champ est seulement surligné — le détail reste dans la liste
 * des modifications.
 *
 * Le DOM réécrit est sous React : on ne touche qu'aux ENFANTS de l'élément, on
 * les remet tels quels au démontage, et un rendu de comparaison est en lecture
 * seule — React n'a pas de raison de les mettre à jour entre-temps.
 */
const useRevisionMarks = (ref, changes, active) => {
  useLayoutEffect(() => {
    const root = ref.current
    const texts = (changes ?? []).filter(c => c.kind === 'text' && Array.isArray(c.path) && c.path.length)
    if (!root || !active || texts.length === 0 || typeof CSS === 'undefined') return undefined
    const done = new Map() // élément → enfants d'origine
    const pending = new Set(texts)

    const findByText = (wanted) => {
      if (!squash(wanted)) return null
      // L'élément le plus PROFOND dont le texte est la nouvelle valeur.
      let best = null
      root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,li,div,strong,em,button,label,blockquote,figcaption,td,th,dt,dd').forEach(el => {
        if (isShown(el) && squash(el.textContent) === squash(wanted) && (!best || best.contains(el))) best = el
      })
      return best
    }

    const mark = (el, change) => {
      done.set(el, [...el.childNodes])
      const wrap = document.createElement('span')
      wrap.className = 'builder-rev'
      for (const part of diffWords(change.before, change.after)) {
        if (part.op === 'same') { wrap.append(part.text); continue }
        const node = document.createElement(part.op)
        node.className = part.op === 'del' ? 'builder-rev-del' : 'builder-rev-ins'
        node.textContent = part.text
        wrap.append(node)
      }
      el.replaceChildren(wrap)
    }

    const apply = () => {
      for (const change of [...pending]) {
        // TOUTES les occurrences : un bloc rend souvent un champ deux fois (menu
        // de bureau + tiroir mobile masqué) — ne marquer que la première
        // laissait la marque sur la copie invisible.
        let els = []
        for (const sel of selectorsFor(change.path)) {
          els = [...root.querySelectorAll(sel)].filter(n => !done.has(n) && isShown(n) && squash(n.textContent) === squash(change.after))
          if (els.length) break
        }
        if (!els.length) {
          const el = findByText(change.after)
          if (el && !done.has(el)) els = [el]
        }
        if (els.length) {
          els.forEach(el => mark(el, change))
          pending.delete(change)
        }
      }
      // Ce qu'on n'a pas pu réécrire est au moins signalé.
      for (const change of pending) {
        for (const sel of selectorsFor(change.path)) root.querySelectorAll(sel).forEach(n => n.classList.add('builder-diff-hit'))
      }
    }
    apply()
    const observer = new MutationObserver((records) => {
      // Nos propres réécritures déclenchent l'observateur : on ne repasse que
      // si le bloc a monté du contenu nouveau ailleurs.
      if (pending.size && records.some(r => ![...done.keys()].some(el => el.contains(r.target)))) apply()
    })
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      done.forEach((children, el) => { if (el.isConnected) el.replaceChildren(...children) })
      root.querySelectorAll('.builder-diff-hit').forEach(n => n.classList.remove('builder-diff-hit'))
    }
  }, [ref, changes, active])
}

/** Largeur de rendu des aperçus : celle d'un écran de bureau. */
const SBS_RENDER_WIDTH = 1280
/** Place prise par la légende d'un aperçu (« Avant · Version du … »). */
const SBS_CAPTION = 28
const SBS_GAP = 16

/**
 * La disposition qui montre le bloc le plus GRAND, et l'échelle qui le fait
 * tenir entier dans sa case.
 *
 * Côte à côte, chaque aperçu a la moitié de la largeur ; l'un sous l'autre, la
 * moitié de la hauteur. Un bandeau large et bas (alerte, en-tête) gagne à être
 * empilé — côte à côte il devient illisible ; une section haute (grille de
 * cartes, formulaire) gagne à être côte à côte. On calcule les deux échelles et
 * on garde la plus grande. Jamais d'agrandissement au-delà de 1.
 */
const sideBySideLayout = (box, contentHeight) => {
  if (!box || !contentHeight) return { layout: 'stack', scale: null }
  const fit = (w, h) => Math.min(1, w / SBS_RENDER_WIDTH, h / contentHeight)
  const side = fit((box.width - SBS_GAP) / 2, box.height - SBS_CAPTION)
  const stack = fit(box.width, (box.height - SBS_GAP) / 2 - SBS_CAPTION)
  return side > stack ? { layout: 'side', scale: side } : { layout: 'stack', scale: stack }
}

/**
 * Pop-in « avant / après » : les deux états du bloc, réduits pour tenir à
 * l'écran — côte à côte ou l'un sous l'autre selon la forme du bloc (cf.
 * sideBySideLayout). Plein écran : c'est un outil de lecture, toute la place
 * va aux aperçus.
 *
 * Rendu isolé (BuilderBlockPreviewFrame : son propre ReactiveJsonRoot, inerte)
 * — aucune interaction avec le canevas, donc aucun conflit d'identité entre
 * les deux montages d'une même unité.
 */
const SideBySideDialog = ({ entry, def, fromLabel, toLabel, onClose }) => {
  const dialogRef = useRef(null)
  const bodyRef = useRef(null)
  const [box, setBox] = useState(null)
  const [heights, setHeights] = useState({})
  useDialogFocus(dialogRef)
  useEscapeClose(onClose)

  useLayoutEffect(() => {
    const node = bodyRef.current
    if (!node) return undefined
    const measure = () => {
      const style = getComputedStyle(node)
      setBox({
        width: node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
        height: node.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom),
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const onBefore = useCallback((h) => setHeights(prev => (prev.before === h ? prev : { ...prev, before: h })), [])
  const onAfter = useCallback((h) => setHeights(prev => (prev.after === h ? prev : { ...prev, after: h })), [])
  // Le plus haut des deux fixe l'échelle : avant et après au MÊME zoom, sinon
  // une différence de taille passerait pour une modification.
  const contentHeight = Math.max(heights.before ?? 0, heights.after ?? 0) || null
  const { layout, scale } = sideBySideLayout(box, contentHeight)

  const columns = [
    { key: 'before', title: 'Avant', sub: fromLabel, bloc: entry.from, onHeight: onBefore },
    { key: 'after', title: 'Après', sub: toLabel, bloc: entry.to, onHeight: onAfter },
  ]
  return createPortal(
    <div className="modal-scrim modal-scrim-full modal-scrim-over-chrome" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-sbs-title"
        className="modal-panel modal-panel-full"
      >
        <div className="modal-header">
          <h2 id="builder-sbs-title" className="text-base font-semibold">
            Avant / après — {def?.name ?? entry.to?.type ?? 'bloc'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            aria-label="Fermer la comparaison côte à côte"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div ref={bodyRef} className={`modal-body builder-sbs-body builder-sbs-${layout}`}>
          {columns.map(col => (
            <figure key={col.key} className="builder-sbs-cell">
              <figcaption className="text-xs text-gray-600">
                <span className={col.key === 'before' ? 'builder-diff-pill-removed' : 'builder-diff-pill-added'}>{col.title}</span>
                <span className="ml-2">{col.sub}</span>
              </figcaption>
              {def?.rjBuild ? (
                // Largeur imposée = largeur de rendu × échelle : le cadre déduit
                // son zoom de sa largeur, il tombe donc exactement sur l'échelle
                // qui fait tenir le bloc ENTIER dans sa case.
                <div style={scale ? { width: Math.floor(SBS_RENDER_WIDTH * scale) } : undefined} className="max-w-full">
                  <BuilderBlockPreviewFrame
                    blocks={[{ key: col.key, block: def, data: col.bloc?.data ?? {} }]}
                    renderWidth={SBS_RENDER_WIDTH}
                    minHeight={48}
                    maxHeight={scale && contentHeight ? Math.ceil(contentHeight * scale) + 2 : 4000}
                    onContentHeight={col.onHeight}
                    className="rounded-lg border border-gray-200"
                  />
                </div>
              ) : (
                <p className="text-xs text-gray-500">Aperçu indisponible pour ce type de bloc.</p>
              )}
            </figure>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Un bloc de la page comparée, dans son cadre de différence.
 *
 * `renderBloc(bloc, side)` est fourni par le canevas : c'est SON montage
 * (BuilderBlockWrapper + BuilderBlockMount), le même que pour la page éditée —
 * un bloc comparé se rend exactement comme il se rendait.
 *
 * Un bloc MODIFIÉ se montre dans son état d'arrivée, ses textes annotés en
 * marques de révision (barré / souligné) ; le reste de ce qui a changé (image,
 * lien, visibilité…) est surligné. L'œil ouvre l'avant et l'après côte à côte,
 * et la liste détaille chaque champ.
 */
export const VersionDiffFrame = ({ entry, renderBloc }) => {
  const { registry, versionView } = useBuilder()
  const [open, setOpen] = useState(false)
  const [sideBySide, setSideBySide] = useState(false)
  const frameRef = useRef(null)
  const sideRef = useRef(null)
  useOverlayHeight(frameRef)
  const status = STATUS[entry.status]
  const modified = entry.status === 'modified'
  const otherTargets = useMemo(
    () => entry.changes.filter(c => Array.isArray(c.path) && c.path.length && c.kind !== 'text').map(targetOf),
    [entry.changes],
  )
  useRevisionMarks(sideRef, entry.changes, modified)
  useFieldHighlights(sideRef, otherTargets, modified)
  const def = useMemo(() => (registry ?? []).find(d => d.type === (entry.to ?? entry.from)?.type), [registry, entry])

  const headingId = `version-diff-${entry.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`
  const shown = entry.status === 'removed' ? entry.from : entry.to

  return (
    <section ref={frameRef} className={`builder-diff-frame ${status.className}`} aria-labelledby={headingId} id={headingId + '-frame'}>
      {entry.status !== 'same' || entry.moved ? (
        <header className="builder-diff-bar">
          <h3 id={headingId} className="builder-diff-badge">{status.label}</h3>
          {entry.moved && (
            <span className="builder-diff-pill-moved"><MoveVertical size={12} aria-hidden="true" /> Déplacé</span>
          )}
          {modified && (
            <>
              <button
                type="button"
                className="builder-diff-eye"
                onClick={() => setSideBySide(true)}
                aria-label="Voir l'avant et l'après côte à côte"
                title="Voir l'avant et l'après côte à côte"
              >
                <Eye size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="builder-diff-toggle"
                aria-expanded={open}
                aria-controls={`${headingId}-changes`}
                onClick={() => setOpen(o => !o)}
              >
                {entry.changes.length} modification{entry.changes.length > 1 ? 's' : ''}
                <ChevronDown size={14} aria-hidden="true" className={open ? 'builder-diff-chevron-open' : undefined} />
              </button>
            </>
          )}
        </header>
      ) : (
        <h3 id={headingId} className="sr-only">Bloc inchangé</h3>
      )}
      {modified && open && (
        <ul id={`${headingId}-changes`} className="builder-diff-changes">
          {entry.changes.map((c, i) => <ChangeItem key={i} change={c} />)}
        </ul>
      )}
      <div ref={sideRef} className="builder-diff-side">
        {renderBloc(shown, entry.status === 'removed' ? 'before' : 'after')}
      </div>
      {sideBySide && (
        <SideBySideDialog
          entry={entry}
          def={def}
          fromLabel={versionName(versionView.from.version)}
          toLabel={versionName(versionView.to.version)}
          onClose={() => setSideBySide(false)}
        />
      )}
    </section>
  )
}

/**
 * En tête de la comparaison : le bilan, les changements de la PAGE (titre,
 * métadonnées — rien à surligner dans un rendu), et des liens vers chaque bloc
 * touché, pour ne pas devoir parcourir une longue page à l'œil.
 */
export const VersionCompareSummary = () => {
  const { versionView } = useBuilder()
  const { stats, pageChanges, entries } = versionView
  const touched = entries.filter(e => e.status !== 'same' || e.moved)


  const label = (n, one, many) => `${n} ${n > 1 ? many : one}`
  const parts = [
    stats.modified && label(stats.modified, 'bloc modifié', 'blocs modifiés'),
    stats.added && label(stats.added, 'bloc ajouté', 'blocs ajoutés'),
    stats.removed && label(stats.removed, 'bloc supprimé', 'blocs supprimés'),
    stats.moved && label(stats.moved, 'bloc déplacé', 'blocs déplacés'),
  ].filter(Boolean)

  return (
    <section className="builder-diff-summary" aria-labelledby="builder-diff-summary-title">
      <h2 id="builder-diff-summary-title" className="text-sm font-semibold text-gray-800 inline-flex items-center gap-1.5">
        <ArrowLeftRight size={15} aria-hidden="true" />
        {parts.length || pageChanges.length ? 'Différences entre les deux versions' : 'Les deux versions sont identiques'}
      </h2>
      {parts.length > 0 && <p className="text-sm text-gray-700 mt-1">{parts.join(' · ')}</p>}
      <p className="builder-diff-legend" aria-hidden="true">
        <span className="builder-diff-pill-added">Ajouté</span>
        <span className="builder-diff-pill-removed">Supprimé</span>
        <span className="builder-diff-pill-modified">Modifié</span>
        <span className="builder-diff-pill-moved">Déplacé</span>
        <span className="text-gray-500">Les éléments modifiés sont surlignés dans le rendu.</span>
      </p>
      {pageChanges.length > 0 && (
        <ul className="builder-diff-changes mt-2">
          {pageChanges.map((c, i) => <ChangeItem key={i} change={c} />)}
        </ul>
      )}
      {touched.length > 0 && (
        <nav aria-label="Blocs concernés" className="mt-2">
          <ul className="flex flex-wrap gap-1.5">
            {touched.map(e => {
              const bloc = e.to ?? e.from
              const id = `version-diff-${e.key.replace(/[^a-zA-Z0-9_-]/g, '-')}-frame`
              return (
                <li key={e.key}>
                  <a href={`#${id}`} className={`builder-diff-jump ${STATUS[e.status].className}`}>
                    {e.status === 'same' ? 'Déplacé' : STATUS[e.status].label} · {bloc?.type ?? 'Bloc'}{e.moved && e.status !== 'same' ? ' (déplacé)' : ''}
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Case « Créer une version » du header
// ---------------------------------------------------------------------------

/**
 * La case « Créer une version » (choix porté par `useVersioningChoice`,
 * versionDiff.js) et son déplieur de commentaire, posés à côté du Save.
 */
export const VersioningToggle = ({ choice, commentOpen, onToggleComment }) => (
  <div className="flex items-center rounded-lg border border-gray-200 focus-within:ring-2 focus-within:ring-teal-500">
    <label
      className={`flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 text-sm font-medium cursor-pointer transition-colors rounded-l-lg ${choice.enabled ? 'text-teal-700 bg-teal-50/40' : 'text-gray-600 hover:text-teal-700 hover:bg-teal-50'}`}
      title="Créer une version à chaque Save — consultable, comparable et restaurable depuis l'historique"
    >
      <input
        type="checkbox"
        checked={choice.enabled}
        onChange={(e) => choice.setEnabled(e.target.checked)}
        className="w-3.5 h-3.5 accent-teal-700 focus-visible:outline-none"
        aria-label="Version : créer une version à chaque Save"
      />
      Version
    </label>
    {choice.enabled && (
      <button
        type="button"
        onClick={onToggleComment}
        aria-expanded={commentOpen}
        aria-controls="builder-version-comment"
        className="px-1.5 py-1.5 border-l border-gray-200 text-gray-500 hover:text-teal-700 hover:bg-teal-50 rounded-r-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-600"
        title="Ajouter un commentaire à la version"
        aria-label="Ajouter un commentaire à la version"
      >
        <ChevronDown size={14} aria-hidden="true" className={commentOpen ? 'builder-diff-chevron-open' : undefined} />
      </button>
    )}
  </div>
)

/** Le champ déplié sous la barre : le commentaire de la prochaine version. */
export const VersionCommentField = ({ choice, onSubmit }) => (
  <div className="mt-2 flex items-center gap-2" id="builder-version-comment">
    <Save size={14} className="text-gray-400 shrink-0" aria-hidden="true" />
    <label htmlFor="builder-version-comment-input" className="text-xs font-medium text-gray-600 shrink-0">
      Commentaire de version
    </label>
    <input
      id="builder-version-comment-input"
      type="text"
      value={choice.comment}
      maxLength={500}
      onChange={(e) => choice.setComment(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit?.() } }}
      placeholder="Facultatif — ex. « Nouvelle offre de rentrée »"
      className="flex-1 min-w-0 max-w-xl px-2 py-1 text-sm text-gray-800 bg-white border border-gray-200 rounded-md focus:border-teal-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
    />
    <span className="text-xs text-gray-500 shrink-0">Enregistré au prochain Save</span>
  </div>
)
